import type { ReactNode } from 'react';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import type {
  TableInputDefinition,
  AliasMatchState,
  ApplyMarketDefaultsResult,
} from '@arcanum-hq/shared-types';
import { DriftBadge } from '@/components/ui/DriftBadge';
import { ToolErrorCallout } from '../ToolErrorCallout';
import { MetricsCard } from './MetricsCard';
import { SensitivityTable } from './SensitivityTable';
import { Sensitivity2DMatrix } from './Sensitivity2DMatrix';
import { ScenarioComparison } from './ScenarioComparison';
import { ScenarioSummaryCard } from './ScenarioSummaryCard';
import { GoalSeekResult } from './GoalSeekResult';
import { DependencyTree } from './DependencyTree';
import { ModelInfoPanel } from './ModelInfoPanel';
import { MarketDefaultApplyReceipt } from './MarketDefaultApplyReceipt';
import { AcceptDefaultsBadge } from './AcceptDefaultsBadge';
import { TableFillProposal } from './TableFillProposal';

// ADR 0007: derive item type from the exported result type (not a barrel-exported internal)
type MarketFieldValueUnit =
  ApplyMarketDefaultsResult['informationalSet'][number];

/**
 * Default no-op for `onApplyToInput` — used until Session 2.4 (builder.tsx)
 * wires the real path-B handler through ChatThread → ToolResultRenderer.
 */
const noOpApplyToInput = (
  _field: MarketFieldValueUnit,
  _aliasState: AliasMatchState
): void => {
  // intentional no-op: Session 2.4 supplies the real callback
};

/**
 * The 5 input-writing tools whose `result.written` map is compared against
 * `inputs[k].value` to drive `<DriftBadge>` (ADR 0019 Decision B,
 * builder-ssot-consolidation Phase 5 Session 5.7). Keyed as a Set for O(1)
 * membership check in the dispatcher.
 */
const DRIFT_BADGE_TOOLS = new Set([
  'set_inputs',
  'set_table_inputs',
  'set_property_address',
  'apply_market_defaults',
  'accept_defaults',
]);

interface ToolResultRendererProps {
  state: ToolCallState;
  /**
   * Synthesized-turn callback. Phase 5 Session 2 (2026-05-08) wires this
   * through to `<TableFillProposal>` so the user's Confirm click POSTs a
   * structured-prefix message back through the existing chat-send pipe.
   * Required because every per-tool render site can in principle host a
   * `propose_table_fill` invocation.
   */
  onSynthesizedTurn: (message: string) => void;
  /**
   * builder-ssot-consolidation Phase 5 Session 5.7 (ADR 0019 Decision B) —
   * SSOT input records from `useConversationViews().data.inputs`. When present,
   * the dispatcher mounts `<DriftBadge>` on the 5 input-writing tools so the
   * user can see when a later edit superseded what this tool wrote. When absent
   * the badge silently no-ops (no error, no render).
   */
  inputs?: Record<string, { value: unknown }> | undefined;
  /**
   * table-input-ux (2026-06-08) — model ID for `useModel(modelId)` lookup
   * inside `<TableFillProposal>` to hydrate the full grid schema. Optional —
   * when absent the proposal falls back to the agent's sparse columns/rows.
   */
  modelId?: string | null;
  /**
   * table-input-ux (2026-06-08) — conversation ID passed to
   * `useTableInputsEdit` for the direct write path. Optional — when absent
   * the proposal falls back to the legacy synthesized-turn path (D2).
   */
  conversationId?: string | null;
  /**
   * table-input-ux (2026-06-08) — resolved `TableInputDefinition` for the
   * table named in the proposal. Looked up from `useModel(modelId).tableInputs`
   * by `<ChatThread>` (or builder.tsx) and passed down. When absent the
   * proposal falls back to the agent's sparse columns/rows.
   */
  tableDef?: TableInputDefinition | null;
  /**
   * For `propose_table_fill` only — whether this proposal already produced a
   * `[table-edit:*]` receipt later in the thread (computed by `<ChatThread>`).
   * Threaded into `<TableFillProposal>` as its durable "already applied" guard.
   */
  alreadyApplied?: boolean;
  /**
   * market-default-apply-ux Phase 2 — read-only gate; threaded from
   * builder.tsx `billingReadOnly || adminReadOnly`. Session 2.4 supplies the
   * real value; 2.2 adds the optional prop so the dispatcher compiles first.
   */
  readOnly?: boolean;
  /**
   * market-default-apply-ux Phase 2 — path-B affordance callback; Session 2.4
   * supplies the real impl; 2.2 plumbs a no-op default at the receipt-mount call.
   */
  onApplyToInput?: (
    field: MarketFieldValueUnit,
    aliasState: AliasMatchState
  ) => void;
  /**
   * table-fill-auto-apply-checkpoint-race fix (2026-06-11) — `true` while the
   * run that produced this card is still streaming. Threaded into
   * `<TableFillProposal>` to GATE the auto-apply until the agent turn finishes.
   * Auto-applying mid-run fires `POST /table-edit` → `appendSystemMessage`,
   * whose hand-fabricated checkpoint write collides on the `messages`-channel
   * version with the agent's still-pending final-message super-step; the
   * PostgresSaver blob de-dupe then silently drops the agent's final summary
   * (it streams live, then vanishes on stream-end/reload). Deferring the apply
   * to post-`done` lets `appendSystemMessage` chain cleanly on top of the
   * settled checkpoint. Default `false` (hydrated/persisted cards never stream).
   */
  runStreaming?: boolean;
}

/**
 * Phase-aware dispatcher (Phase 2 Session 3).
 *
 * Routes on `state.phase` first (errored → <ToolErrorCallout>), then on
 * `state.tool` for the per-tool render. Per-tool renderers handle the
 * remaining 4 phases internally (skeleton / in-progress / complete /
 * aborted) per directive §7 + Phase 2's `aborted` extension.
 *
 * Hidden tools (return null at any phase):
 *   - `propose_plan` — surfaced as <PlanChecklist> in Session 4 (NOT a card)
 *   - `market_data_enrichment` — narrated in agent prose; raw card is noise
 *
 * Badge tools (render only when `phase === 'complete'`):
 *   - `apply_market_defaults` → <MarketDefaultApplyReceipt> (market-default-apply-ux Phase 2)
 *   - `accept_defaults` → <AcceptDefaultsBadge>
 *
 * The `data-tool-result` + `data-phase` attributes are exposed for DOM queries
 * (used by Playwright smoke spec + ui-reviewer runtime checks + the e2e-walk
 * runbook in Session 6).
 */
export function ToolResultRenderer({
  state,
  onSynthesizedTurn,
  inputs,
  modelId,
  conversationId,
  tableDef,
  alreadyApplied,
  readOnly,
  onApplyToInput,
  runStreaming,
}: ToolResultRendererProps) {
  // Errored phase: shared callout regardless of tool.
  if (state.phase === 'errored') {
    return (
      <div
        data-tool-result={state.tool}
        data-phase="errored"
        className="contents"
      >
        <ToolErrorCallout
          tool={state.tool}
          code={state.error.code}
          message={state.error.message}
          details={state.error.details}
        />
      </div>
    );
  }

  const inner = renderPerTool(
    state,
    onSynthesizedTurn,
    inputs,
    modelId,
    conversationId,
    tableDef,
    alreadyApplied,
    readOnly,
    onApplyToInput,
    runStreaming
  );
  if (inner === null) return null;

  // builder-ssot-consolidation Phase 5 Session 5.7 (ADR 0019 Decision B):
  // mount <DriftBadge> on the 5 input-writing tools at complete-phase so the
  // user sees when a later edit superseded what this tool wrote. The badge
  // self-no-ops when `written`/`inputs` is absent or no value drifted.
  const driftWritten =
    state.phase === 'complete' && DRIFT_BADGE_TOOLS.has(state.tool)
      ? (state.result as { written?: Record<string, unknown> } | undefined)
          ?.written
      : undefined;

  return (
    <div
      data-tool-result={state.tool}
      data-phase={state.phase}
      className="contents"
    >
      {inner}
      <DriftBadge written={driftWritten} inputs={inputs} />
    </div>
  );
}

function renderPerTool(
  state: ToolCallState,
  onSynthesizedTurn: (message: string) => void,
  inputs: Record<string, { value: unknown }> | undefined,
  modelId: string | null | undefined,
  conversationId: string | null | undefined,
  tableDef: TableInputDefinition | null | undefined,
  alreadyApplied: boolean | undefined,
  readOnly: boolean | undefined,
  onApplyToInput:
    | ((field: MarketFieldValueUnit, aliasState: AliasMatchState) => void)
    | undefined,
  runStreaming: boolean | undefined
): ReactNode {
  switch (state.tool) {
    // `generate_excel` REMOVED 2026-05-20 — tool no longer exists. Pre-removal
    // conversations with persisted generate_excel tool-result envelopes fall
    // through to the `default` case below (renders the generic envelope) since
    // the ToolName union no longer carries `generate_excel`.
    case 'set_inputs':
    case 'calculate':
    case 'get_values':
    case 'validate_input':
    case 'export_sensitivity_tab':
    case 'set_table_inputs':
      return <MetricsCard state={state} />;
    case 'run_sensitivity':
      return <SensitivityTable state={state} />;
    case 'run_sensitivity_2d':
      return <Sensitivity2DMatrix state={state} />;
    case 'compare_scenarios': {
      // Phase 3 Session 4 (2026-05-07): result schema is now a
      // discriminated union on `kind`. Route compact_3axis to the new
      // summary card; matrix (default) keeps the legacy comparison table.
      // Phase 4 Session 3 (2026-05-07): during `executing`, route by the
      // `scenarios_started.kind` payload so the in-progress paint matches
      // the eventual result shape (compact_3axis fills 3 fixed rows;
      // matrix paints columns one-at-a-time).
      if (
        state.phase === 'complete' &&
        typeof state.result === 'object' &&
        state.result !== null &&
        'kind' in state.result &&
        (state.result as { kind: unknown }).kind === 'compact_3axis'
      ) {
        return <ScenarioSummaryCard state={state} />;
      }
      if (state.phase === 'executing') {
        const startedEvent = state.progress.find(
          (p) => p.phase === 'scenarios_started'
        );
        const kind = (startedEvent?.payload as { kind?: string } | undefined)
          ?.kind;
        if (kind === 'compact_3axis') {
          return <ScenarioSummaryCard state={state} />;
        }
      }
      return <ScenarioComparison state={state} />;
    }
    case 'goal_seek':
      return <GoalSeekResult state={state} />;
    case 'get_dependencies':
      return <DependencyTree state={state} />;
    case 'get_model_info':
      return <ModelInfoPanel state={state} />;
    case 'apply_market_defaults':
      // Phase 4 Session 2 — show executing-phase progress card so the user
      // sees "Looking up defaults for {deal_type}, {location}…" then
      // "Applied N defaults" before the receipt takes over.
      // reload survival via deriveToolCallStates/m.toolCallStates — see ADR 0002; do not mount this receipt outside <ToolResultRenderer>.
      if (state.phase === 'complete')
        return (
          <MarketDefaultApplyReceipt
            result={state.result as ApplyMarketDefaultsResult}
            conversationId={conversationId ?? ''}
            readOnly={readOnly}
            onApplyToInput={onApplyToInput ?? noOpApplyToInput}
          />
        );
      if (state.phase === 'executing') return <MetricsCard state={state} />;
      return null;
    case 'accept_defaults':
      return state.phase === 'complete' ? (
        <AcceptDefaultsBadge result={state.result} />
      ) : null;
    case 'market_data_enrichment':
      // Phase 4 Session 2 — show executing-phase tier ticker. Post-result
      // stays hidden (the data flows into apply_market_defaults; a separate
      // post-result card is noise).
      if (state.phase === 'executing') return <MetricsCard state={state} />;
      return null;
    case 'propose_table_fill':
      // table-input-ux (2026-06-08) — schema-hydrated full grid + direct write.
      // `modelId` + `conversationId` + `tableDef` + `currentInputs` enable the
      // new direct-write path; when absent falls back to legacy synthesized-turn
      // (D2 back-compat).
      return (
        <TableFillProposal
          state={state}
          modelId={modelId}
          conversationId={conversationId}
          currentInputs={inputs}
          tableDef={tableDef}
          alreadyApplied={alreadyApplied}
          runStreaming={runStreaming}
          onSynthesizedTurn={onSynthesizedTurn}
        />
      );
    case 'propose_plan':
      // propose_plan surfaced as <PlanChecklist> in Session 4 (NOT a tool-result card).
      return null;
    default:
      return null;
  }
}
